import { useState, useEffect, useRef } from "react";
import Dashboard from "./components/Dashboard";
import ProjectView from "./components/ProjectView";
import CRMView from "./components/CRMView";
import ExpertsView from "./components/ExpertsView";
import AnalyticsView from "./components/AnalyticsView";
import Login from "./Login";
import AdminDashboard from "./components/AdminDashboard";
import ManagerDashboard from "./components/ManagerDashboard";
import SetPassword from "./components/SetPassword";
import SettingsView from "./components/SettingsView";
import SourceView from "./components/SourceView";
import SchedulingView from "./components/SchedulingView";
import RaspAIAllView from "./components/RaspAIAllView";
import "./App.css";

// ENRICH-AUTO-1: how often pop-up notices are refreshed while the app is open.
export const NOTIF_POLL_MS = 30000;

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const NAV = [
  { id: "dashboard",  label: "Dashboard",  icon: "▦" },
  { id: "crm",        label: "Inbox",      icon: "✉" },
  { id: "source",     label: "Source",     icon: "◈" },
  { id: "scheduling", label: "Scheduling", icon: "◔" },
  { id: "experts",    label: "Experts",    icon: "◎" },
];

export default function App() {
  const [page, setPage] = useState("dashboard");
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [, setNotifChecked] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sourceEverOpened, setSourceEverOpened] = useState(false);
  const [credits, setCredits] = useState(null);

  const [inviteToken, setInviteToken] = useState(() => new URLSearchParams(window.location.search).get('invite'));

  useEffect(() => {
    const token = localStorage.getItem("expertnet_token");
    const userStr = localStorage.getItem("expertnet_user");
    if (token && userStr) {
      fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        if (res.ok) {
          setCurrentUser(JSON.parse(userStr));
          fetchNotifications();
          fetchCredits();
        } else {
          localStorage.removeItem("expertnet_token");
          localStorage.removeItem("expertnet_user");
        }
      }).catch(() => {
        localStorage.removeItem("expertnet_token");
        localStorage.removeItem("expertnet_user");
      }).finally(() => {
        setAuthChecked(true);
      });
    } else {
      setAuthChecked(true);
    }
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
    fetchNotifications();
    fetchProjects();
    fetchUnreadCount();
    fetchCredits();
  };

  const handleLogout = () => {
    localStorage.removeItem("expertnet_token");
    localStorage.removeItem("expertnet_user");
    setCurrentUser(null);
  };

  // ENRICH-AUTO-1 (D-48): ids of enrichment notices already announced, so a repeat
  // poll does not re-fire the finished event for the same notice.
  const announcedEnrichRef = useRef(new Set());
  const fetchNotifications = async () => {
    const token = localStorage.getItem('expertnet_token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/notifications/my`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const rows = await res.json();
        setNotifications(rows);
        const fresh = (rows || []).filter(n => n.type === 'enrich_done' && !announcedEnrichRef.current.has(n.id));
        if (fresh.length) {
          fresh.forEach(n => announcedEnrichRef.current.add(n.id));
          window.dispatchEvent(new Event('rasp:credits-changed'));
          fresh.forEach(n => window.dispatchEvent(new CustomEvent('rasp:enrich-finished', { detail: { project_id: n.project_id } })));
        }
      }
    } catch (e) {}
    setNotifChecked(true);
  };

  const dismissNotification = async (id) => {
    const token = localStorage.getItem('expertnet_token');
    await fetch(`${API}/notifications/${id}/dismiss`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  useEffect(() => {
    fetchProjects();
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    // ENRICH-AUTO-1: pop-up notices were only loaded at login; the poller writes
    // them while the user is already in the app, so refresh them too.
    const notifInterval = setInterval(fetchNotifications, NOTIF_POLL_MS);
    return () => { clearInterval(interval); clearInterval(notifInterval); };
  }, []); // eslint-disable-line

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setProjects(await res.json());
    } catch (e) {
      console.error("Failed to fetch projects:", e);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('expertnet_token');
      const res = await fetch(`${API}/crm/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count || 0);
      }
    } catch (e) {
      console.error("Failed to fetch unread count:", e);
    }
  };

  const fetchCredits = async () => {
    const token = localStorage.getItem('expertnet_token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/credits/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setCredits(typeof data.balance === 'number' ? data.balance : null);
      }
    } catch (e) {}
  };

  useEffect(() => {
    const onFocus = () => fetchCredits();
    const onCreditsChanged = () => fetchCredits();
    window.addEventListener('focus', onFocus);
    window.addEventListener('rasp:credits-changed', onCreditsChanged);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('rasp:credits-changed', onCreditsChanged);
    };
  }, []);

  const handleSelectProject = (p) => {
    if (p && p.id) { setSelectedProject(p); setPage("project"); }
  };

  const handleNavigate = (p) => {
    if (p === 'source') setSourceEverOpened(true);
    setPage(p);
    if (p === "dashboard") { setSelectedProject(null); fetchProjects(); }
  };

  if (!authChecked) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "var(--amber)", fontSize: 14 }}>Loading...</span>
      </div>
    );
  }

  if (!currentUser) {
    if (inviteToken) {
      return (
        <SetPassword
          token={inviteToken}
          onDone={() => {
            const url = new URL(window.location.href);
            url.searchParams.delete('invite');
            window.history.replaceState({}, '', url.toString());
            setInviteToken(null);
          }}
        />
      );
    }
    return <Login onLogin={handleLogin} />;
  }

  if (currentUser.role === 'admin') {
    return <AdminDashboard currentUser={currentUser} onLogout={handleLogout} />;
  }

  if (currentUser.role === 'manager') {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', width: '100vw', background: 'var(--bg)' }}>
        <ManagerDashboard currentUser={currentUser} onLogout={handleLogout} />
      </div>
    );
  }

  const getBreadcrumb = () => {
    const sep = <span style={{ color: 'var(--text-5)', margin: '0 6px' }}>/</span>;
    if (page === 'dashboard') return <span>Dashboard</span>;
    if (page === 'project') return (
      <>
        <span
          onClick={() => handleNavigate('dashboard')}
          style={{ cursor: 'pointer', color: 'var(--text-3)' }}
        >Dashboard</span>
        {sep}
        <span>{selectedProject?.name || 'Project'}</span>
      </>
    );
    if (page === 'rasp-ai-all') return (
      <>
        <span
          onClick={() => handleNavigate('dashboard')}
          style={{ cursor: 'pointer', color: 'var(--text-3)' }}
        >Dashboard</span>
        {sep}
        <span>Rasp AI</span>
      </>
    );
    if (page === 'scheduling') return <span>Scheduling</span>;
    if (page === 'crm')        return <span>Inbox</span>;
    if (page === 'experts')    return <span>Experts</span>;
    if (page === 'source')     return <span>Source</span>;
    if (page === 'analytics')  return <span>Analytics</span>;
    if (page === 'settings')   return <span>Settings</span>;
    return null;
  };

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>

      {/* Pop-up notification toasts */}
      {notifications.map((n, index) => (
        <div key={n.id} style={{ position: 'fixed', top: `${16 + index * 80}px`, right: 16, zIndex: 9999, background: 'var(--bg2)', border: '1px solid var(--amber)', borderRadius: 8, padding: '12px 16px', maxWidth: 340, display: 'flex', alignItems: 'flex-start', gap: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
          <span style={{ color: 'var(--t2)', fontSize: 13, flex: 1 }}>{n.message}</span>
          <button onClick={() => dismissNotification(n.id)} style={{ background: 'none', border: 'none', color: 'var(--amber)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
      ))}

      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? '240px' : '60px',
        flexShrink: 0,
        transition: 'width .22s cubic-bezier(.2,.6,.2,1)',
        background: 'var(--glass-sidebar)',
        backdropFilter: 'var(--glass-blur)',
        WebkitBackdropFilter: 'var(--glass-blur)',
        borderRight: '1px solid var(--line)',
        display: 'flex',
        flexDirection: 'column',
        padding: '14px 10px',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        zIndex: 10,
      }}>

        {/* Logo */}
        <div style={{ padding: '6px 2px 18px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <svg width="28" height="28" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="rasp-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#2dd4bf" />
                  <stop offset="1" stopColor="#0e7490" />
                </linearGradient>
              </defs>
              <rect x="2" y="2" width="40" height="40" rx="12" fill="url(#rasp-grad)" />
              <circle cx="22" cy="18" r="6.4" fill="none" stroke="#fff" strokeWidth="3" />
              <path d="M22 24.4v6.2" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
              <path d="M22 30.6l5 4M22 30.6l-5 4" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
            </svg>
            {sidebarOpen && (
              <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                Rasp
              </span>
            )}
          </div>
        </div>

        {/* Nav items */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {NAV.map(({ id, label, icon }) => {
            const active = page === id || (id === "dashboard" && (page === "project" || page === "rasp-ai-all"));
            const showBadge = id === 'crm' && unreadCount > 0;
            return (
              <div
                key={id}
                onClick={() => handleNavigate(id)}
                title={!sidebarOpen ? label : undefined}
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: sidebarOpen ? 'flex-start' : 'center',
                  gap: sidebarOpen ? '8px' : '0',
                  height: '34px',
                  padding: sidebarOpen ? '0 10px' : '0',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: active ? 'var(--text-1)' : 'var(--text-3)',
                  background: active
                    ? 'linear-gradient(90deg, rgba(45,212,191,.22), rgba(45,212,191,.04))'
                    : 'transparent',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Icon with optional collapsed badge */}
                <span style={{
                  fontSize: '14px',
                  color: active ? 'var(--accent)' : 'var(--text-4)',
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}>
                  {icon}
                  {!sidebarOpen && showBadge && (
                    <span style={{
                      position: 'absolute', top: '-4px', right: '-6px',
                      background: 'var(--negative)', color: '#fff',
                      fontSize: '9px', fontWeight: '600',
                      fontFamily: 'var(--font-data)',
                      borderRadius: '999px', minWidth: '14px', height: '14px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 3px', lineHeight: 1,
                    }}>
                      {unreadCount}
                    </span>
                  )}
                </span>
                {/* Label (open state only) */}
                {sidebarOpen && <span style={{ flex: 1 }}>{label}</span>}
                {/* Inline badge (open state) */}
                {sidebarOpen && showBadge && (
                  <span style={{
                    background: 'var(--negative)', color: '#fff',
                    fontSize: '10px', fontWeight: '600',
                    fontFamily: 'var(--font-data)',
                    borderRadius: '999px', minWidth: '16px', height: '16px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', lineHeight: 1,
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        {/* Spacer + Recent Projects (open state only) */}
        <div style={{ marginTop: 'auto' }}>
          {sidebarOpen && projects.length > 0 && (
            <div style={{ paddingBottom: '8px' }}>
              <div className="r-eyebrow" style={{ padding: '16px 10px 6px' }}>Recent projects</div>
              {projects.slice(0, 3).map((p) => (
                <div
                  key={p.id}
                  onClick={() => handleSelectProject(p)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    height: '30px', padding: '0 10px', borderRadius: '6px',
                    cursor: 'pointer', overflow: 'hidden',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {p.ticker && (
                    <span style={{
                      fontFamily: 'var(--font-data)', fontSize: '10px',
                      color: 'var(--accent)', flexShrink: 0,
                    }}>
                      {p.ticker}
                    </span>
                  )}
                  <span style={{
                    fontSize: '12px', color: 'var(--text-3)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ borderTop: '1px solid var(--line)', paddingTop: '4px' }}>
            <div
              onClick={() => setSidebarOpen(o => !o)}
              title={!sidebarOpen ? 'Expand' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                height: '32px', padding: sidebarOpen ? '0 10px' : '0',
                justifyContent: sidebarOpen ? 'flex-start' : 'center',
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', color: 'var(--text-3)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: '11px', color: 'var(--text-4)' }}>
                {sidebarOpen ? '◁' : '▷'}
              </span>
              {sidebarOpen && <span>Collapse</span>}
            </div>
            <div
              onClick={handleLogout}
              title={!sidebarOpen ? 'Log out' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                height: '32px', padding: sidebarOpen ? '0 10px' : '0',
                justifyContent: sidebarOpen ? 'flex-start' : 'center',
                borderRadius: '8px', cursor: 'pointer',
                fontSize: '13px', color: 'var(--text-3)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: '11px', color: 'var(--text-4)' }}>⏻</span>
              {sidebarOpen && <span>Log out</span>}
            </div>
          </div>
        </div>

      </div>{/* /Sidebar */}

      {/* Right column */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>

        {/* Top bar */}
        <div style={{
          height: '52px',
          flexShrink: 0,
          background: 'var(--glass-topbar)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          borderBottom: '1px solid var(--line)',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 5,
        }}>
          <div style={{ fontSize: '13px', color: 'var(--text-1)', display: 'flex', alignItems: 'center' }}>
            {getBreadcrumb()}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div id="topbar-actions" />
            {credits !== null && (
              <span
                data-testid="credits-counter"
                data-zero={credits === 0 ? 'true' : undefined}
                style={{
                  fontSize: '12px',
                  color: credits === 0 ? 'var(--red, #f87171)' : 'var(--text-2)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {credits.toLocaleString('en-US')} credits
              </span>
            )}
          </div>
        </div>

        {/* Scrollable page content */}
        <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          {page === "dashboard" && (
            <Dashboard
              API={API}
              onSelectProject={handleSelectProject}
              onNavigate={handleNavigate}
            />
          )}
          {page === "project" && selectedProject && (
            <ProjectView
              project={selectedProject}
              onBack={() => handleNavigate("dashboard")}
              onNavigate={handleNavigate}
            />
          )}
          {page === "scheduling" && (
            <SchedulingView API={API} />
          )}
          {page === "crm" && (
            <CRMView API={API} currentUser={currentUser} />
          )}
          {page === "experts" && (
            <ExpertsView API={API} />
          )}
          {page === "rasp-ai-all" && (
            <RaspAIAllView
              API={API}
              projects={projects}
              onSelectProject={handleSelectProject}
            />
          )}
          {sourceEverOpened && (
            <div style={{ display: page === 'source' ? undefined : 'none' }}>
              <SourceView />
            </div>
          )}
          {page === "analytics" && (
            <AnalyticsView token={localStorage.getItem('expertnet_token')} />
          )}
          {page === "settings" && (
            <SettingsView currentUser={currentUser} API={API} />
          )}
        </div>

      </div>{/* /Right column */}

    </div>
  );
}
